import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pencil, Trash2, CheckCircle, RotateCcw, UserCheck } from 'lucide-react';

export const UserManagementTable = ({ members, lang, t, onEdit, onDelete, onToggleMatch, onUnclaim }) => (
  <div className="glass-card rounded-xl overflow-hidden overflow-x-auto" data-testid="user-management-table">
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent" style={{ borderColor: 'var(--border-color)' }}>
          <TableHead className="t-text-muted text-xs">{t('photo')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('name')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('gender')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('age')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('domicile')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('category')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('occupation')}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('whatsapp')}</TableHead>
          <TableHead className="t-text-muted text-xs">{lang === 'en' ? 'Claim' : 'Klaim'}</TableHead>
          <TableHead className="t-text-muted text-xs text-right">{t('actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map(m => (
          <TableRow key={m.id} className="hover:bg-[var(--bg-input)]" style={{ borderColor: 'var(--border-color)' }} data-testid={`member-row-${m.id}`}>
            <TableCell>
              <div className="w-10 h-10 rounded-lg overflow-hidden t-bg-input">
                {m.photos?.[0] ? <img src={m.photos[0]} alt="" className="w-full h-full object-cover" /> : null}
              </div>
            </TableCell>
            <TableCell className="t-text text-sm font-medium">{m.name || '-'}</TableCell>
            <TableCell className="t-text-secondary text-sm capitalize">{m.gender || '-'}</TableCell>
            <TableCell className="t-text-secondary text-sm">{m.age || '-'}</TableCell>
            <TableCell className="t-text-secondary text-sm">{m.domicile || '-'}</TableCell>
            <TableCell><span className="inline-block text-xs bg-[#D98A94]/10 text-[#D98A94] px-2 py-0.5 rounded-full whitespace-nowrap">{m.category || '-'}</span></TableCell>
            <TableCell className="t-text-secondary text-sm">{m.occupation || '-'}</TableCell>
            <TableCell className="t-text-secondary text-sm">{m.whatsapp || '-'}</TableCell>
            <TableCell>
              {m.claimed_by ? (
                <div className="flex items-center gap-1.5" title={m.claimed_by.whatsapp || m.claimed_by.email || ''}>
                  <UserCheck className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-[11px] text-green-600 font-semibold truncate max-w-[110px]">
                    {m.claimed_by.whatsapp || m.claimed_by.email || (lang === 'en' ? 'Claimed' : 'Diklaim')}
                  </span>
                </div>
              ) : (
                <span className="t-text-muted text-xs">-</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => onToggleMatch(m.id)} data-testid={`match-member-${m.id}`} className={`p-2 rounded-lg transition ${m.is_matched ? 'bg-green-500/10 text-green-500' : 'hover:bg-green-500/10 t-text-muted hover:text-green-500'}`} title={m.is_matched ? 'Matched' : 'Mark as Matched'}>
                  <CheckCircle className="w-4 h-4" />
                </button>
                {onUnclaim && m.claimed_by && (
                  <button
                    onClick={() => onUnclaim(m)}
                    data-testid={`unclaim-member-${m.id}`}
                    className="p-2 rounded-lg hover:bg-[#F59E0B]/10 t-text-muted hover:text-[#F59E0B] transition"
                    title={lang === 'en' ? 'Reset claim (allow re-claim)' : 'Reset klaim (bisa diklaim ulang)'}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => onEdit(m)} data-testid={`edit-member-${m.id}`} className="p-2 rounded-lg hover:bg-[var(--bg-input)] t-text-muted hover:text-[#C7867A] transition">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => onDelete(m)} data-testid={`delete-member-${m.id}`} className="p-2 rounded-lg hover:bg-red-500/10 t-text-muted hover:text-red-500 transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {members.length === 0 && (
          <TableRow><TableCell colSpan={10} className="text-center t-text-muted py-10">{lang === 'en' ? 'No members yet' : 'Belum ada anggota'}</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);

// Format ISO string → dd/mm/yyyy (Indonesian preferred format across the admin dashboard)
const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export const AccountsTable = ({ accounts, lang, t, onUnclaim }) => (
  <div className="glass-card rounded-xl overflow-hidden overflow-x-auto" data-testid="accounts-table">
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent" style={{ borderColor: 'var(--border-color)' }}>
          <TableHead className="t-text-muted text-xs">{lang === 'en' ? 'WhatsApp / Email' : 'WhatsApp / Email'}</TableHead>
          <TableHead className="t-text-muted text-xs">{t('name')}</TableHead>
          <TableHead className="t-text-muted text-xs">{lang === 'en' ? 'Claimed Profile' : 'Profil Diklaim'}</TableHead>
          <TableHead className="t-text-muted text-xs">{lang === 'en' ? 'Claim Date' : 'Tanggal Klaim'}</TableHead>
          <TableHead className="t-text-muted text-xs">{lang === 'en' ? 'Joined' : 'Bergabung'}</TableHead>
          <TableHead className="t-text-muted text-xs text-right">{t('actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {accounts.map(a => (
          <TableRow key={a.id} className="hover:bg-[var(--bg-input)]" style={{ borderColor: 'var(--border-color)' }} data-testid={`account-row-${a.id}`}>
            <TableCell className="t-text text-sm">{a.whatsapp || a.email || '-'}</TableCell>
            <TableCell className="t-text-secondary text-sm">{a.name || '-'}</TableCell>
            <TableCell className="text-sm">
              {a.claimed_profile ? (
                <div className="flex items-center gap-2 min-w-[180px]">
                  {a.claimed_profile.photo ? (
                    <img src={a.claimed_profile.photo} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg t-bg-input flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="t-text font-medium truncate">{a.claimed_profile.name || '-'}</div>
                    <span className="text-[10px] bg-[#D98A94]/10 text-[#D98A94] px-1.5 py-0.5 rounded-full">
                      {a.claimed_profile.category || '-'}
                    </span>
                  </div>
                </div>
              ) : (
                <span className="t-text-muted text-xs">-</span>
              )}
            </TableCell>
            <TableCell className="text-sm">
              {a.claimed_at ? (
                <span className="t-text-secondary font-mono text-xs" data-testid={`claim-date-${a.id}`}>{formatDate(a.claimed_at)}</span>
              ) : (
                <span className="t-text-muted text-xs">-</span>
              )}
            </TableCell>
            <TableCell className="t-text-muted text-sm font-mono text-xs">{formatDate(a.created_at)}</TableCell>
            <TableCell className="text-right">
              {onUnclaim && a.claimed_profile ? (
                <button
                  onClick={() => onUnclaim(a)}
                  data-testid={`unclaim-account-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20 text-xs font-semibold transition"
                  title={lang === 'en' ? 'Reset claim (allow another member to claim)' : 'Reset klaim (biar bisa diklaim yang benar)'}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {lang === 'en' ? 'Reset Claim' : 'Reset Klaim'}
                </button>
              ) : (
                <span className="t-text-muted text-xs">-</span>
              )}
            </TableCell>
          </TableRow>
        ))}
        {accounts.length === 0 && (
          <TableRow><TableCell colSpan={6} className="text-center t-text-muted py-10">{lang === 'en' ? 'No accounts match' : 'Tidak ada akun'}</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);
